import { ListFilter } from "lucide-react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "components/common/dropdown/DropDownV2";
import { cn } from "utils/cn";
import {
  effectiveCommentPanelFilters,
  isCommentPanelFilterActive,
} from "../scriptCommentsPanelFilters";
import type { CommentPanelFilters } from "../scriptCommentsPanelFilters.types";
import type { ScriptCommentsPanelFilterMenuProps } from "./ScriptCommentsPanelFilterMenu.types";

export const ScriptCommentsPanelFilterMenu = ({
  filters,
  onFiltersChange,
  showFeatureFilter,
  readFilterUnavailable = false,
}: ScriptCommentsPanelFilterMenuProps) => {
  // Shows the filters that apply; changes still start from the chosen ones, so a paused read
  // filter comes back once read times load.
  const appliedFilters = effectiveCommentPanelFilters(filters, !readFilterUnavailable);
  const filterActive = isCommentPanelFilterActive(appliedFilters);

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Filter comments"
          className={cn(
            "relative flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground transition hover:bg-muted hover:text-foreground",
            filterActive && "bg-muted/50 text-hue-sky"
          )}
        >
          <ListFilter className="h-4 w-4" aria-hidden />
          {filterActive ? (
            <span
              className="absolute right-1.5 top-1.5 h-1.5 w-1.5 rounded-full bg-hue-sky"
              aria-hidden
            />
          ) : null}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={6}
        className="min-w-[11rem] max-w-[min(calc(100vw-2rem),16rem)]"
      >
        {showFeatureFilter ? (
          <>
            <DropdownMenuLabel className="text-xs uppercase tracking-wider text-muted-foreground">
              Feature
            </DropdownMenuLabel>
            <DropdownMenuRadioGroup
              value={filters.feature}
              onValueChange={(value) =>
                onFiltersChange({
                  ...filters,
                  feature: value as CommentPanelFilters["feature"],
                })
              }
            >
              <DropdownMenuRadioItem value="all">All features</DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="screenplay">Script</DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="schedule">Schedule</DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>
            <DropdownMenuSeparator className="bg-muted/70" />
          </>
        ) : null}

        <DropdownMenuLabel className="text-xs uppercase tracking-wider text-muted-foreground">
          Status
        </DropdownMenuLabel>
        <DropdownMenuRadioGroup
          value={filters.status}
          onValueChange={(value) =>
            onFiltersChange({
              ...filters,
              status: value as CommentPanelFilters["status"],
            })
          }
        >
          <DropdownMenuRadioItem value="all">All statuses</DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="open">Unresolved</DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="resolved">Resolved</DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>

        <DropdownMenuSeparator className="bg-muted/70" />

        <DropdownMenuLabel className="text-xs uppercase tracking-wider text-muted-foreground">
          Read
        </DropdownMenuLabel>
        <DropdownMenuRadioGroup
          value={appliedFilters.read}
          onValueChange={(value) =>
            onFiltersChange({
              ...filters,
              read: value as CommentPanelFilters["read"],
            })
          }
        >
          <DropdownMenuRadioItem value="all">All threads</DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="unread" disabled={readFilterUnavailable}>
            Unread
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="read" disabled={readFilterUnavailable}>
            Read
          </DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
